import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 5000;

export default function ChatManager() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/chat/conversations`, { withCredentials: true });
      setConversations(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const res = await axios.get(`${API}/admin/chat/${convId}/messages`, { withCredentials: true });
      setMessages(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchConversations();
    const id = setInterval(fetchConversations, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedConv) return;
    fetchMessages(selectedConv.conversation_id);
    const id = setInterval(() => fetchMessages(selectedConv.conversation_id), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [selectedConv, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedConv) return;
    setSending(true);
    const text = reply.trim();
    setReply('');
    try {
      await axios.post(`${API}/admin/chat/${selectedConv.conversation_id}/reply`, { text }, { withCredentials: true });
      fetchMessages(selectedConv.conversation_id);
      fetchConversations();
    } catch (e) { toast.error('Erreur'); }
    finally { setSending(false); }
  };

  const handleDelete = async (convId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await axios.delete(`${API}/admin/chat/${convId}`, { withCredentials: true });
      toast.success('Conversation supprimée');
      if (selectedConv?.conversation_id === convId) {
        setSelectedConv(null);
        setMessages([]);
      }
      fetchConversations();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="chat-manager">
      <h2 className="text-2xl font-bold text-[#2E332D] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Conversations Chat ({conversations.length})
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Conversations List */}
        <div className="card max-h-[600px] overflow-y-auto">
          {loading && <p className="text-center text-[#5C665A] py-4">Chargement...</p>}
          {!loading && conversations.length === 0 && (
            <p className="text-center text-[#5C665A] py-8 text-sm">Aucune conversation</p>
          )}
          {!loading && conversations.length > 0 && (
            <div className="space-y-2">
              {conversations.map((c) => {
                const isSelected = selectedConv?.conversation_id === c.conversation_id;
                return (
                  <div
                    key={c.conversation_id}
                    onClick={() => setSelectedConv(c)}
                    data-testid={`conv-item-${c.conversation_id}`}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'bg-[#4A6B48] text-white' : 'hover:bg-[#F9F8F6]'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User size={14} />
                          <p className="font-semibold text-sm truncate">{c.visitor_name}</p>
                        </div>
                        {c.visitor_email && (
                          <p className={`text-xs truncate mt-1 ${isSelected ? 'text-white/80' : 'text-[#5C665A]'}`}>{c.visitor_email}</p>
                        )}
                        <p className={`text-xs mt-1 ${isSelected ? 'text-white/70' : 'text-[#5C665A]'}`}>
                          {new Date(c.last_message_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      </div>
                      {c.unread_admin > 0 && !isSelected && (
                        <span className="bg-[#D87D4A] text-white text-xs px-2 py-0.5 rounded-full font-bold">
                          {c.unread_admin}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Conversation Detail */}
        <div className="card flex flex-col h-[600px]">
          {!selectedConv && (
            <div className="flex-1 flex items-center justify-center text-[#5C665A]">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-2 opacity-30" />
                <p>Sélectionnez une conversation</p>
              </div>
            </div>
          )}

          {selectedConv && (
            <>
              <div className="border-b border-[#E5E3DB] pb-3 mb-3 flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-[#2E332D]">{selectedConv.visitor_name}</h3>
                  {selectedConv.visitor_email && (
                    <p className="text-sm text-[#5C665A]">{selectedConv.visitor_email}</p>
                  )}
                </div>
                <button onClick={() => handleDelete(selectedConv.conversation_id)} className="p-2 hover:bg-red-50 rounded text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                {messages.map((m) => {
                  const isAdmin = m.from_role === 'admin';
                  return (
                    <div key={m.message_id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3 py-2 ${
                        isAdmin ? 'bg-[#4A6B48] text-white' : 'bg-[#F9F8F6] text-[#2E332D]'
                      }`}>
                        <p className="text-xs font-semibold mb-1 opacity-80">{m.sender_name}</p>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-xs mt-1 ${isAdmin ? 'text-white/70' : 'text-[#5C665A]'}`}>
                          {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleReply} className="border-t border-[#E5E3DB] pt-3 flex gap-2">
                <input
                  type="text"
                  placeholder="Votre réponse..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                  data-testid="admin-chat-reply-input"
                />
                <button type="submit" disabled={sending || !reply.trim()} className="btn-primary px-4 disabled:opacity-50" data-testid="admin-chat-send-button">
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
